import React, { useMemo, useState } from "react";
import {
  BsArrowRepeat,
  BsCheckCircleFill,
  BsChevronDown,
  BsChevronUp,
  BsDownload,
  BsExclamationCircleFill,
  BsX,
  BsXCircle,
} from "react-icons/bs";
import { useTheme } from "../../context/ThemeProvider";
import { useDownloadQueue } from "../../context/DownloadQueueContext";
import { ACTIVE_DOWNLOAD_STATUSES, DOWNLOAD_STATUS } from "../../utils/downloadTransport";
import "./DownloadQueuePanel.css";

const FALLBACK_REASON_TEXT = {
  MASTER_BELOW_4K: "The original file for this clip is below 4K, so you're getting HD.",
  MASTER_UNVERIFIED: "We couldn't verify this clip is true 4K, so you're getting HD.",
  MASTER_MISSING: "This clip has no 4K file, so you're getting HD.",
  MASTER_UNAVAILABLE: "The 4K file is unavailable right now, so you're getting HD.",
};

export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const value = n / 1024 ** exponent;
  return `${value >= 100 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
}

function formatDuration(ms) {
  const seconds = Math.max(0, Math.round(ms / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
}

const percentOf = (received, total) =>
  total > 0 ? Math.min(100, Math.floor((received / total) * 100)) : null;

export function ProgressBar({ percent, label }) {
  const indeterminate = percent === null || percent === undefined;
  return (
    <div
      className={`download-queue-progress${indeterminate ? " download-queue-progress--indeterminate" : ""}`}
      role="progressbar"
      aria-label={label}
      aria-valuemin={indeterminate ? undefined : 0}
      aria-valuemax={indeterminate ? undefined : 100}
      aria-valuenow={indeterminate ? undefined : percent}
      data-indeterminate={indeterminate ? "true" : "false"}
    >
      <div
        className="download-queue-progress__bar"
        style={indeterminate ? undefined : { width: `${percent}%` }}
      />
    </div>
  );
}

function statusLine(item) {
  switch (item.status) {
    case DOWNLOAD_STATUS.QUEUED:
      return "Queued";
    case DOWNLOAD_STATUS.PREPARING:
      return "Preparing — checking quality and file";
    case DOWNLOAD_STATUS.DOWNLOADING: {
      const pct = percentOf(item.bytesReceived, item.totalBytes);
      if (pct === null) return `Downloading · ${formatBytes(item.bytesReceived)}`;
      return `Downloading · ${formatBytes(item.bytesReceived)} of ${formatBytes(item.totalBytes)} · ${pct}%`;
    }
    case DOWNLOAD_STATUS.COMPLETED: {
      if (item.mode === "browser") {
        return "Handed to your browser — track it in your browser's downloads";
      }
      const took = item.startedAt && item.finishedAt
        ? ` in ${formatDuration(item.finishedAt - item.startedAt)}`
        : "";
      return `Completed · ${formatBytes(item.totalBytes || item.bytesReceived)}${took}`;
    }
    case DOWNLOAD_STATUS.CANCELLED:
      return "Cancelled";
    case DOWNLOAD_STATUS.FAILED:
      return "Failed";
    default:
      return "";
  }
}

function QualityBadge({ item }) {
  const delivered = item.quality;
  const requested = item.requestedQuality || "4K";
  const base = "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold leading-none border";
  if (!delivered) {
    return (
      <span className={`${base} border-current opacity-70`} title={`Requested ${requested}`}>
        {requested}
      </span>
    );
  }
  if (delivered === requested) {
    return (
      <span className={`${base} border-[var(--primary)] text-[var(--primary)]`}>
        {delivered}
      </span>
    );
  }
  return (
    <span
      className={`${base} border-amber-500 text-amber-500`}
      title={FALLBACK_REASON_TEXT[item.fallbackReason] || `Requested ${requested}`}
    >
      {requested} → {delivered}
    </span>
  );
}

function DownloadItem({ item, darkMode, onCancel, onRetry, onDismiss }) {
  const [thumbFailed, setThumbFailed] = useState(false);
  const active = ACTIVE_DOWNLOAD_STATUSES.has(item.status);
  const title = item.title || "Shotkut video";
  const failed = item.status === DOWNLOAD_STATUS.FAILED;
  const completed = item.status === DOWNLOAD_STATUS.COMPLETED;

  let percent = null;
  if (item.status === DOWNLOAD_STATUS.DOWNLOADING) {
    percent = percentOf(item.bytesReceived, item.totalBytes);
  } else if (completed) {
    percent = 100;
  }
  const showBar = active || completed;

  const iconButton = `p-1.5 rounded-md transition-colors ${
    darkMode ? "hover:bg-white/10" : "hover:bg-black/5"
  }`;

  return (
    <li
      className={`flex gap-3 py-3 ${darkMode ? "border-[#2A2C2B]" : "border-[#E2E2E2]"} border-t first:border-t-0`}
      data-testid="download-item"
      data-status={item.status}
    >
      <div
        className={`relative h-10 w-16 shrink-0 overflow-hidden rounded ${
          darkMode ? "bg-[#2A2C2B]" : "bg-[#E6E6E6]"
        }`}
      >
        {item.thumbnail && !thumbFailed ? (
          <img
            src={item.thumbnail}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
            onError={() => setThumbFailed(true)}
          />
        ) : (
          <BsDownload className="absolute inset-0 m-auto text-[var(--grey)]" aria-hidden="true" />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium" title={title}>
            {title}
          </p>
          <QualityBadge item={item} />
        </div>

        <p
          className={`mt-0.5 text-xs ${failed ? "text-red-500" : "text-[var(--grey)]"} flex items-center gap-1`}
          aria-live="polite"
        >
          {completed && <BsCheckCircleFill className="shrink-0 text-[var(--primary)]" aria-hidden="true" />}
          {failed && <BsExclamationCircleFill className="shrink-0" aria-hidden="true" />}
          <span>{statusLine(item)}</span>
        </p>

        {failed && item.error && (
          <p className="mt-0.5 text-xs text-red-500" role="alert">
            {item.error}
          </p>
        )}

        {item.quality && item.quality !== (item.requestedQuality || "4K") && item.fallbackReason && !failed && (
          <p className="mt-0.5 text-[11px] text-[var(--grey)]">
            {FALLBACK_REASON_TEXT[item.fallbackReason]}
          </p>
        )}

        {showBar && (
          <div className="mt-2">
            <ProgressBar percent={percent} label={`${title} progress`} />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-start gap-0.5">
        {active && (
          <button
            type="button"
            className={iconButton}
            onClick={() => onCancel(item.id)}
            aria-label={`Cancel download of ${title}`}
            title="Cancel"
          >
            <BsXCircle />
          </button>
        )}
        {(failed || item.status === DOWNLOAD_STATUS.CANCELLED) && item.retryable !== false && (
          <button
            type="button"
            className={iconButton}
            onClick={() => onRetry(item.id)}
            aria-label={`Retry download of ${title}`}
            title="Retry"
          >
            <BsArrowRepeat />
          </button>
        )}
        {!active && (
          <button
            type="button"
            className={iconButton}
            onClick={() => onDismiss(item.id)}
            aria-label={`Dismiss ${title}`}
            title="Dismiss"
          >
            <BsX />
          </button>
        )}
      </div>
    </li>
  );
}

export default function DownloadQueuePanel() {
  const { darkMode } = useTheme();
  const {
    items,
    expanded,
    setExpanded,
    cancelDownload,
    retryDownload,
    dismissDownload,
    clearFinished,
  } = useDownloadQueue();

  const summary = useMemo(() => {
    const active = items.filter((i) => ACTIVE_DOWNLOAD_STATUSES.has(i.status));
    const finished = items.length - active.length;
    const failed = items.filter((i) => i.status === DOWNLOAD_STATUS.FAILED).length;
    const transferring = active.filter((i) => i.status !== DOWNLOAD_STATUS.QUEUED);
    const allKnown =
      transferring.length > 0 &&
      transferring.every((i) => i.status === DOWNLOAD_STATUS.DOWNLOADING && i.totalBytes > 0);
    const overallPercent = allKnown
      ? percentOf(
          transferring.reduce((sum, i) => sum + (i.bytesReceived || 0), 0),
          transferring.reduce((sum, i) => sum + i.totalBytes, 0),
        )
      : null;
    return { active, finished, failed, overallPercent };
  }, [items]);

  if (items.length === 0) return null;

  const { active, finished, failed, overallPercent } = summary;
  let headline;
  if (active.length > 0) {
    const queued = active.filter((i) => i.status === DOWNLOAD_STATUS.QUEUED).length;
    const running = active.length - queued;
    headline = [
      running ? `${running} downloading` : null,
      queued ? `${queued} queued` : null,
    ].filter(Boolean).join(" · ");
  } else if (failed > 0) {
    headline = `${failed} failed`;
  } else {
    headline = "All downloads finished";
  }

  const surface = darkMode
    ? "bg-[#1B1D1C]/95 border-[#333333] text-white"
    : "bg-white/95 border-[#CBCBCB] text-[#333333]";

  return (
    <section
      aria-label="Downloads"
      data-testid="download-queue-panel"
      className={`fixed z-[45] top-[66px] left-2 right-2 sm:left-auto sm:right-4 sm:w-[380px] rounded-lg border shadow-xl backdrop-blur ${surface}`}
    >
      <header className="flex items-center gap-3 px-3 py-2.5">
        <BsDownload className="shrink-0 text-[var(--primary)]" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">Downloads</p>
          <p className="truncate text-xs text-[var(--grey)]" aria-live="polite">
            {headline}
          </p>
        </div>
        {finished > 0 && (
          <button
            type="button"
            onClick={clearFinished}
            className="shrink-0 text-xs text-[var(--grey)] hover:text-[var(--primary)]"
          >
            Clear finished
          </button>
        )}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className={`shrink-0 p-1.5 rounded-md ${darkMode ? "hover:bg-white/10" : "hover:bg-black/5"}`}
          aria-expanded={expanded}
          aria-label={expanded ? "Minimize downloads" : "Expand downloads"}
        >
          {expanded ? <BsChevronUp /> : <BsChevronDown />}
        </button>
      </header>

      {active.length > 0 && (
        <div className="px-3 pb-2">
          <ProgressBar percent={overallPercent} label="Overall download progress" />
        </div>
      )}

      {expanded && (
        <ul className="max-h-[50vh] sm:max-h-[60vh] overflow-y-auto px-3 pb-1">
          {items.map((item) => (
            <DownloadItem
              key={item.id}
              item={item}
              darkMode={darkMode}
              onCancel={cancelDownload}
              onRetry={retryDownload}
              onDismiss={dismissDownload}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
